import React, {
  FC,
  useContext,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import CloseIcon from '@Assets/SVG/Close'
import { Image } from '@Components/Cloudinary/Image'
import { SVGIcon } from '@Components/Icons'
import { MetadataContext } from '@Globals/Metadata'
import { storageAvailable } from '@Globals/Storage'
import { down, up } from '@Globals/Style/breakpoints'
import {
  box,
  flexCenterChildren,
  flexCenterChildrenColumn,
  sectionInner,
  sectionOuter,
} from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

const LS_COOKIE_BAR_CLOSED = 'cookie-bar-closed'

const CookieBarWrapper = styled.section`
  ${sectionOuter}
  background-color: ${({ theme }) => theme.colors.greyLightest};
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: ${({ theme }) => theme.zIndexes.cookieBar};

  ${up('desktop')} {
    position: relative;
    z-index: ${({ theme }) => theme.zIndexes.header};
  }

  .inner {
    ${sectionInner}
    ${flexCenterChildrenColumn}
    position: relative;

    ${down('desktop')} {
      padding: 0 40px 0 10px;
    }

    .closeIcon {
      ${flexCenterChildren}
      position: absolute;
      top: 0;
      right: 0;
      ${box({ height: '100%', width: '40px' })};
      padding: 13px;
      cursor: pointer;

      ${down('desktop')} {
        padding: 13px;
      }

      svg {
        fill: ${({ theme }) => theme.colors.grey};
      }

      &:hover {
        svg {
          fill: ${({ theme }) => theme.colors.greyDark};
        }
      }
    }

    .messageWrap {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 700px;
      min-height: 60px;
      padding: 15px 0;

      img {
        width: 25px;
        height: 25px;
        margin-right: 10px;
        opacity: 0.3;
      }

      .textWrap {
        line-height: 1;

        ${down('desktop')} {
          span, strong {
            line-height: 1.5;
          }
        }

        span, a {
          font-size: 0.75rem;
          line-height: 1;
          color: ${({ theme }) => theme.colors.grey};
        }

        .cookieText {
          padding-right: 6px;

          a {
            text-decoration: underline;
          }
        }

        .acceptText {
          text-decoration: underline;
          cursor: pointer;
          white-space: nowrap;
        }
      }
    }
  }
`

export const CookieBar: FC = () => {
  const { t, tDanger } = useTranslation()
  const { cmsData, store } = useContext(MetadataContext)
  const [showCookieBar, setShowCookieBar] = useState(false)

  useEffect(() => {
    if (!storageAvailable('localStorage')) {
      return
    }

    if (window.localStorage.getItem(LS_COOKIE_BAR_CLOSED)) {
      return
    }
    setShowCookieBar(true)
  }, [])

  const onCloseButtonHandler = () => {
    localStorage.setItem(LS_COOKIE_BAR_CLOSED, '1')
    setShowCookieBar(false)
  }

  if (!showCookieBar || !cmsData?.urls?.cookiePolicy || store?.config?.usercentricsEnabled) {
    return null
  }

  return (
    <CookieBarWrapper>
      <div className="inner">
        <div className="messageWrap">
          <Image options={{ path: 'frontend/random-images/cookie-icon.svg' }} />
          <div className="textWrap">
            <span
              className="cookieText"
              {...tDanger('header:COOKIE_TEXT', { arg1: cmsData?.urls?.cookiePolicy })}
            />
            <span className="acceptText" onClick={onCloseButtonHandler}>
              {t('header:I_ACCEPT')}
            </span>
          </div>
        </div>
        <div className="closeIcon" onClick={onCloseButtonHandler}>
          <SVGIcon icon={CloseIcon} size="14px" />
        </div>
      </div>
    </CookieBarWrapper>
  )
}
